/**
 * 导出错误处理工具
 * 统一处理导出过程中的错误
 */

/**
 * 导出错误类型
 */
export enum ExportErrorType {
  FORMAT_NOT_SUPPORTED = 'FORMAT_NOT_SUPPORTED',
  CONTENT_TOO_LARGE = 'CONTENT_TOO_LARGE',
  NETWORK_ERROR = 'NETWORK_ERROR',
  BROWSER_NOT_SUPPORTED = 'BROWSER_NOT_SUPPORTED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INVALID_CONTENT = 'INVALID_CONTENT',
  GENERATION_FAILED = 'GENERATION_FAILED',
  DOWNLOAD_FAILED = 'DOWNLOAD_FAILED',
  USER_CANCELLED = 'USER_CANCELLED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * 导出错误类
 */
export class ExportError extends Error {
  public readonly type: ExportErrorType
  public readonly code?: string
  public readonly details?: any

  constructor(
    type: ExportErrorType,
    message: string,
    code?: string,
    details?: any
  ) {
    super(message)
    this.name = 'ExportError'
    this.type = type
    this.code = code
    this.details = details
  }
}

/**
 * 错误信息映射
 */
const errorMessages: Record<ExportErrorType, string> = {
  [ExportErrorType.FORMAT_NOT_SUPPORTED]: '不支持的导出格式',
  [ExportErrorType.CONTENT_TOO_LARGE]: '内容过大，无法导出',
  [ExportErrorType.NETWORK_ERROR]: '网络错误，请检查网络连接',
  [ExportErrorType.BROWSER_NOT_SUPPORTED]: '当前浏览器不支持此功能',
  [ExportErrorType.PERMISSION_DENIED]: '没有权限执行此操作',
  [ExportErrorType.INVALID_CONTENT]: '内容格式不正确',
  [ExportErrorType.GENERATION_FAILED]: '文件生成失败',
  [ExportErrorType.DOWNLOAD_FAILED]: '文件下载失败',
  [ExportErrorType.USER_CANCELLED]: '用户取消了导出操作',
  [ExportErrorType.UNKNOWN_ERROR]: '未知错误'
}

/**
 * 用户友好的错误解决建议
 */
const errorSolutions: Record<ExportErrorType, string> = {
  [ExportErrorType.FORMAT_NOT_SUPPORTED]: '请选择其他支持的导出格式',
  [ExportErrorType.CONTENT_TOO_LARGE]: '请减少内容或分段导出',
  [ExportErrorType.NETWORK_ERROR]: '请检查网络连接后重试',
  [ExportErrorType.BROWSER_NOT_SUPPORTED]: '请升级浏览器或使用其他浏览器',
  [ExportErrorType.PERMISSION_DENIED]: '请检查浏览器权限设置',
  [ExportErrorType.INVALID_CONTENT]: '请检查内容格式是否正确',
  [ExportErrorType.GENERATION_FAILED]: '请重试或联系技术支持',
  [ExportErrorType.DOWNLOAD_FAILED]: '请检查浏览器下载设置',
  [ExportErrorType.USER_CANCELLED]: '导出已取消',
  [ExportErrorType.UNKNOWN_ERROR]: '请重试或联系技术支持'
}

/**
 * 处理导出错误
 */
export const handleExportError = (error: unknown): ExportError => {
  if (error instanceof ExportError) {
    return error
  }

  if (error instanceof Error) {
    // 根据错误消息判断错误类型
    if (error.message.includes('not supported')) {
      return new ExportError(
        ExportErrorType.FORMAT_NOT_SUPPORTED,
        error.message,
        'FORMAT_ERROR',
        { originalError: error }
      )
    }

    if (error.message.includes('network') || error.message.includes('fetch')) {
      return new ExportError(
        ExportErrorType.NETWORK_ERROR,
        error.message,
        'NETWORK_ERROR',
        { originalError: error }
      )
    }

    if (error.message.includes('permission') || error.message.includes('denied')) {
      return new ExportError(
        ExportErrorType.PERMISSION_DENIED,
        error.message,
        'PERMISSION_ERROR',
        { originalError: error }
      )
    }

    if (error.message.includes('large') || error.message.includes('size')) {
      return new ExportError(
        ExportErrorType.CONTENT_TOO_LARGE,
        error.message,
        'SIZE_ERROR',
        { originalError: error }
      )
    }

    // 默认生成失败错误
    return new ExportError(
      ExportErrorType.GENERATION_FAILED,
      error.message,
      'GENERATION_ERROR',
      { originalError: error }
    )
  }

  // 未知错误
  return new ExportError(
    ExportErrorType.UNKNOWN_ERROR,
    '发生未知错误',
    'UNKNOWN_ERROR',
    { originalError: error }
  )
}

/**
 * 获取用户友好的错误信息
 */
export const getUserFriendlyErrorMessage = (error: ExportError): {
  title: string
  message: string
  solution: string
} => {
  const title = '导出失败'
  const message = error.message || errorMessages[error.type]
  const solution = errorSolutions[error.type]

  return {
    title,
    message,
    solution
  }
}

/**
 * 记录导出错误
 */
export const logExportError = (error: ExportError, context?: {
  format?: string
  contentSize?: number
  userAgent?: string
}): void => {
  const logData = {
    type: error.type,
    message: error.message,
    code: error.code,
    details: error.details,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent
  }

  console.error('导出错误:', logData)

  // 这里可以添加错误上报逻辑
  // 例如发送到错误监控服务
}

/**
 * 检查浏览器兼容性
 */
export const checkBrowserCompatibility = (): {
  isSupported: boolean
  missingFeatures: string[]
} => {
  const missingFeatures: string[] = []

  // 检查必要的 API
  if (typeof Blob === 'undefined') {
    missingFeatures.push('Blob API')
  }

  if (typeof URL === 'undefined' || !URL.createObjectURL) {
    missingFeatures.push('URL API')
  }

  if (typeof document === 'undefined' || !document.createElement) {
    missingFeatures.push('DOM API')
  }

  // 检查 Canvas 支持（图片导出需要）
  try {
    const canvas = document.createElement('canvas')
    if (!canvas.getContext) {
      missingFeatures.push('Canvas API')
    }
  } catch {
    missingFeatures.push('Canvas API')
  }

  return {
    isSupported: missingFeatures.length === 0,
    missingFeatures
  }
}